/**
 * QuirkyQode SSO AuthContext for Influence Marketing
 * 
 * Auth flow:
 * 1. On mount, check URL for ?qq_token=<jwt> (SSO launch from client portal)
 * 2. Store token in localStorage
 * 3. Verify with QQ backend → get client info + subscription status
 * 4. If locked/expired → set qqLocked state (SoftwareLock screen shown)
 * 5. If valid → set user state normally
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const QQ_API = process.env.REACT_APP_QQ_API_URL || 'http://localhost:6000';
const QQ_TOOL = 'influence_marketing';

async function verifyQQToken(token) {
  const resp = await axios.get(`${QQ_API}/api/auth/verify-tool-token`, {
    params: { token, tool: QQ_TOOL },
  });
  return resp.data;
}

function decodeJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error('Failed to decode JWT:', err);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [qqClient, setQqClient] = useState(null);
  const [qqLocked, setQqLocked] = useState(false);
  const [qqLockStatus, setQqLockStatus] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function initAuth() {
      let token = null;
      try {
        // 1. Check URL for QQ SSO token
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('qq_token');
        if (urlToken) {
          localStorage.setItem('qq_token', urlToken);
          // Clean URL
          urlParams.delete('qq_token');
          urlParams.delete('client_id');
          const newSearch = urlParams.toString();
          window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
        }

        token = localStorage.getItem('qq_token');
        if (!token) { setLoading(false); return; }

        // 2. Verify with QQ server
        const data = await verifyQQToken(token);
        if (!data.valid) { localStorage.removeItem('qq_token'); setAuthError('Token verification returned invalid'); setLoading(false); return; }

        // 3. Check subscription status
        const status = data.status || 'suspended';
        if (status === 'suspended' || status === 'expired') {
          setQqLocked(true);
          setQqLockStatus(status);
          setQqClient({ company_name: data.company_name });
          setLoading(false);
          return;
        }

        if (!data.tool_access) {
          setQqLocked(true);
          setQqLockStatus('no_access');
          setLoading(false);
          return;
        }

        // 4. Decode the JWT payload locally to extract user profile and role
        const decoded = decodeJwt(token);
        if (!decoded) {
          localStorage.removeItem('qq_token');
          setAuthError('Failed to parse token payload');
          setLoading(false);
          return;
        }

        // Determine tool-specific role:
        // If owner ('client'), they are always 'admin'
        // If team member ('org_user'), read it from the decoded tool_access list
        let toolRole = 'admin';
        if (decoded.role === 'org_user') {
          const ta = (decoded.tool_access || []).find(a => a.tool_id === QQ_TOOL);
          toolRole = ta ? ta.role : 'viewer';
        }

        // Set the active user profile (use org_user details if available, otherwise company details)
        setUser({
          id: decoded.role === 'org_user' ? decoded.id : decoded.client_id || decoded.id,
          email: decoded.email,
          name: decoded.role === 'org_user' ? decoded.name : decoded.company_name,
          company_name: decoded.company_name,
          role: toolRole,
          qq_token: token,
        });
        setQqClient({ company_name: decoded.company_name });
      } catch (err) {
        console.error('[qqAuth] Verification failed:', err.message);
        setAuthError(`Verification failed: ${err.message}. (QQ_API: "${QQ_API}", Token length: ${token ? token.length : 0})`);
        localStorage.removeItem('qq_token');
      } finally {
        setLoading(false);
      }
    }

    initAuth();
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('qq_token');
    setUser(null);
    setQqClient(null);
    setQqLocked(false);
    setAuthError(null);
    // Redirect to QQ portal
    window.location.href = process.env.REACT_APP_QQ_PORTAL_URL || 'http://localhost:7001/my-tools';
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, qqClient, qqLocked, qqLockStatus, logout, loading, authError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
